import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Result,
  Modal,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import documentService from '../services/documentService';
import {
  renderCustomFieldInput,
  getCustomFieldValuePropName,
  parseCustomFieldValueForForm,
  serializeCustomFieldFormValue,
  isCustomFieldValueEmpty,
  getCustomFieldAnswerComment,
} from '../components/documents/customFieldTypes';
import {
  calculateFindings,
  fieldCollectsComment,
  fieldCollectsImage,
  fieldRequiresComment,
  fieldRequiresImage,
  fieldSupportsEvidence,
  getAnswerColor,
  isEvidenceRequired,
  isInspectionType,
  resolveInspectionStatuses,
} from '../components/documents/documentFindings';
import FieldEvidence from '../components/documents/FieldEvidence';
import QuestionStatusBadge from '../components/documents/QuestionStatusBadge';
import FindingsStrip from '../components/documents/FindingsStrip';
import { toBase64Payload } from '../components/documents/documentImages';
import { warmHeicConverter } from '../utils/imageCompression';
import {
  SYNC_LOCAL,
  SYNC_PENDING,
  deleteDraft,
  getDraft,
  getDraftImages,
  newDraftId,
  putDraft,
  syncDraftImages,
} from '../utils/documentDraftStore';
import { enqueueDraft, isRetryableError } from '../utils/documentOutbox';
import '../assets/styles/documents.css';

const { Text, Title } = Typography;

const fieldFormName = (fieldId) => `documentField_${fieldId}`;

// A repeating group's answers are one set per captured row, so its fields need a
// form name per row. Same naming as the portal's fill screen.
const rowFieldFormName = (fieldId, row) => `documentField_${fieldId}__row${row}`;

const EMPTY_EVIDENCE = { comment: '', files: [] };

/**
 * Create or edit a document.
 */
const DocumentFill = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { templateId: routeTemplateId, documentId: routeDocumentId } = useParams();

  const {
    templateName,
    lockedCompanyId,
    relationId,
    returnTo,
    completedReturnTo,
    inProgressReturnTo,
    draftId: resumeDraftId,
  } = location.state ?? {};

  const [form] = Form.useForm();
  const [statusId, setStatusId] = useState(undefined);
  const [companyId, setCompanyId] = useState(undefined);
  const [evidence, setEvidence] = useState({});
  // How many rows each repeating group is showing, keyed by group id.
  const [rowCounts, setRowCounts] = useState({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);
  const [fields, setFields] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [typeName, setTypeName] = useState('');
  const [hasRating, setHasRating] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [savedImages, setSavedImages] = useState([]);
  const [unavailableOffline, setUnavailableOffline] = useState(false);

  const isEdit = !!routeDocumentId;

  // Offline Variables
  const draftIdRef = useRef(resumeDraftId ?? newDraftId());
  const syncStateRef = useRef(SYNC_LOCAL);
  const restoredFilesRef = useRef({});
  const objectUrlsRef = useRef([]);
  const seededRef = useRef(false);
  const finalizedRef = useRef(false);
  const baselineRef = useRef(null);
  const persistedRef = useRef(false);

  // Fetch the HEIC converter now, while there is a connection: it is a lazy
  // chunk, and an iPhone photo attached later in a dead zone needs it.
  useEffect(() => {
    if (navigator.onLine !== false) warmHeicConverter();
  }, []);

  useEffect(
    () => () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current = [];
    },
    []
  );

  useEffect(() => {
    let cancelled = false;

    // A draft carries its own form definition, so it reopens with no network.
    const loadFromDraft = async () => {
      const draft = await getDraft(draftIdRef.current);
      if (!draft) return false;

      const images = await getDraftImages(draft.draftId);
      const restored = {};

      images.forEach((image) => {
        const file = new File([image.blob], image.name, { type: image.type });
        file.uid = image.uid;

        const thumbUrl = URL.createObjectURL(image.blob);
        objectUrlsRef.current.push(thumbUrl);

        (restored[image.fieldKey] ??= []).push({
          uid: image.uid,
          name: image.name,
          type: image.type,
          size: image.blob.size,
          status: 'done',
          percent: 100,
          originFileObj: file,
          thumbUrl,
        });
      });

      if (cancelled) return true;

      syncStateRef.current = draft.syncState ?? SYNC_LOCAL;
      restoredFilesRef.current = restored;

      setFields(draft.form?.fields ?? []);
      setStatuses(draft.form?.statuses ?? []);
      setTypeName(draft.form?.typeName ?? '');
      setHasRating(draft.form?.hasRating ?? false);
      setCompanies(draft.form?.companies ?? []);
      setSavedImages(draft.savedImages ?? []);
      setExisting({
        name: draft.name,
        fieldValues: draft.fieldValues ?? [],
        currentStatusId: draft.statusId ?? null,
        companyId: draft.companyId,
        documentTemplateId: draft.templateId,
      });

      return true;
    };

    const load = async () => {
      setLoading(true);
      setUnavailableOffline(false);

      try {
        if (await loadFromDraft()) return;

        const document = isEdit ? await documentService.getDocument(routeDocumentId) : null;
        const templateId = isEdit ? document?.documentTemplateId : routeTemplateId;

        if (!templateId) throw new Error('No template for this document');

        // Statuses and hasRating come from available-templates rather than the
        // builder API: it is the endpoint scoped to what this user can reach,
        // and it already carries both.
        const [templates, templateFields, images, eligibleCompanies] = await Promise.all([
          documentService.getAvailableTemplates(lockedCompanyId),
          documentService.getTemplateFields(templateId),
          isEdit ? documentService.getDocumentImages(routeDocumentId) : Promise.resolve([]),
          isEdit ? Promise.resolve([]) : documentService.getTemplateCompanies(templateId),
        ]);

        if (cancelled) return;

        const template = templates.find((entry) => entry.id === templateId);

        setExisting(document);
        setFields(templateFields);
        setStatuses(template?.statuses ?? []);
        setTypeName(template?.typeName ?? '');
        setHasRating(template?.hasRating ?? false);
        setSavedImages(images);
        setCompanies(eligibleCompanies);
      } catch (error) {
        console.error('Failed to load document', error);
        if (cancelled) return;

        if (navigator.onLine === false || isRetryableError(error)) {
          setUnavailableOffline(true);
        } else {
          message.error('Failed to load document');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isEdit, routeDocumentId, routeTemplateId, lockedCompanyId]);

  const fieldGroups = useMemo(() => {
    const groups = [];

    fields.forEach((field) => {
      const group = groups.find((entry) => entry.id === field.documentTemplateGroupId);
      if (group) {
        group.fields.push(field);
        return;
      }
      groups.push({
        id: field.documentTemplateGroupId,
        name: field.groupName,
        hint: field.groupHint,
        isRepeating: !!field.groupIsRepeating,
        fields: [field],
      });
    });

    return groups;
  }, [fields]);

  // The groups whose answers are captured as rows, and the fields inside them.
  const tableGroups = useMemo(
    () => fieldGroups.filter((group) => group.isRepeating),
    [fieldGroups]
  );

  const tableFieldIds = useMemo(() => {
    const ids = new Set();
    tableGroups.forEach((group) => group.fields.forEach((field) => ids.add(field.id)));
    return ids;
  }, [tableGroups]);

  const savedImagesByField = useMemo(() => {
    const byField = {};
    savedImages.forEach((image) => {
      if (!image.fieldKey) return;
      (byField[image.fieldKey] ??= []).push(image);
    });
    return byField;
  }, [savedImages]);

  /**
   Check whether a document has been changed to be marked as Draft
   */
  const signatureOf = useCallback(
    (values, held, status, company) =>
      JSON.stringify({
        name: values.documentName ?? '',
        status: status ?? null,
        company: company ?? null,
        fields: fields.map((field) => ({
          id: field.id,
          value: serializeCustomFieldFormValue(field, values[fieldFormName(field.id)]) ?? null,
          comment: held[field.id]?.comment?.trim() ?? '',
          photos: (held[field.id]?.files ?? []).map((file) => file.uid).sort(),
        })),
      }),
    [fields]
  );

  // Row answers live under their own form names, so they are signed separately and
  // added to the signature above rather than changing how it is built.
  const rowSignatureOf = useCallback(
    (values) =>
      JSON.stringify(
        Object.entries(values ?? {})
          .filter(([name]) => name.includes('__row'))
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([name, value]) => [name, value ?? null])
      ),
    []
  );

  // Seed the form once everything has landed.
  useEffect(() => {
    if (loading) return;

    const savedByFieldId = new Map(
      (existing?.fieldValues ?? []).map((entry) => [
        entry.documentTemplateCustomFieldId,
        entry.fieldValues,
      ])
    );

    const values = { documentName: existing?.name ?? templateName ?? '' };

    fields.forEach((field) => {
      values[fieldFormName(field.id)] = parseCustomFieldValueForForm(
        field,
        savedByFieldId.get(field.id)
      );
    });

    form.setFieldsValue(values);

    const seeded = {};
    fields.forEach((field) => {
      const comment = getCustomFieldAnswerComment(field, savedByFieldId.get(field.id));
      if (comment) seeded[field.id] = { comment, files: [] };
    });

    Object.entries(restoredFilesRef.current).forEach(([fieldId, files]) => {
      seeded[fieldId] = { comment: seeded[fieldId]?.comment ?? '', files };
    });
    restoredFilesRef.current = {};

    setEvidence(seeded);

    const seededStatusId =
      existing?.currentStatusId ??
      [...statuses].sort((a, b) => (a.statusOrder ?? 0) - (b.statusOrder ?? 0))[0]?.id;

    const seededCompanyId =
      existing?.companyId ??
      lockedCompanyId ??
      (companies.length === 1 ? companies[0].id : undefined);

    setStatusId(seededStatusId);
    setCompanyId(seededCompanyId);

    baselineRef.current =
      signatureOf(values, seeded, seededStatusId, seededCompanyId) + rowSignatureOf(values);
    seededRef.current = true;
  }, [
    loading, fields, statuses, companies, existing, templateName, lockedCompanyId, form,
    signatureOf, rowSignatureOf,
  ]);

  // Table rows are seeded on their own, so the plain seeding above is untouched.
  // A row answer is the one that carries a rowIndex; rows are numbered from 1.
  useEffect(() => {
    if (loading) return;

    const rowValues = {};
    const counts = {};

    (existing?.fieldValues ?? []).forEach((entry) => {
      if (entry.rowIndex == null) return;

      const field = fields.find((item) => item.id === entry.documentTemplateCustomFieldId);
      if (!field) return;

      rowValues[rowFieldFormName(field.id, entry.rowIndex)] = parseCustomFieldValueForForm(
        field,
        entry.fieldValues
      );

      const groupId = field.documentTemplateGroupId;
      if (entry.rowIndex > (counts[groupId] ?? 0)) counts[groupId] = entry.rowIndex;
    });

    setRowCounts(counts);
    if (Object.keys(rowValues).length > 0) form.setFieldsValue(rowValues);
  }, [loading, fields, existing, form]);

  const watchedValues = Form.useWatch([], form);

  const readFormValue = useCallback(
    (field) =>
      watchedValues
        ? watchedValues[fieldFormName(field.id)]
        : form.getFieldValue(fieldFormName(field.id)),
    [watchedValues, form]
  );

  const readEvidence = useCallback((fieldId) => evidence[fieldId] ?? EMPTY_EVIDENCE, [evidence]);

  const readRowValue = useCallback(
    (field, row) =>
      watchedValues
        ? watchedValues[rowFieldFormName(field.id, row)]
        : form.getFieldValue(rowFieldFormName(field.id, row)),
    [watchedValues, form]
  );

  // A row nobody has typed in is not saved at all.
  const isRowEmpty = useCallback(
    (group, row) =>
      group.fields.every((field) =>
        isCustomFieldValueEmpty(serializeCustomFieldFormValue(field, readRowValue(field, row)))
      ),
    [readRowValue]
  );

  const isCompressing = useMemo(
    () =>
      Object.values(evidence).some((held) =>
        (held.files ?? []).some((file) => file.status === 'uploading')
      ),
    [evidence]
  );

  const hasPhoto = useCallback(
    (fieldId) =>
      (savedImagesByField[fieldId]?.length ?? 0) > 0 || (evidence[fieldId]?.files.length ?? 0) > 0,
    [savedImagesByField, evidence]
  );

  const selectedStatus = useMemo(
    () => statuses.find((status) => status.id === statusId),
    [statuses, statusId]
  );

  const isInspection = isInspectionType(typeName);
  const inspectionStatuses = useMemo(() => resolveInspectionStatuses(statuses), [statuses]);

  const lockedCompanyName =
    existing?.companyName ??
    companies.find((company) => company.id === (existing?.companyId ?? lockedCompanyId))?.name ??
    existing?.companyId ??
    lockedCompanyId ??
    '—';

  const isCompleting = isInspection || !!selectedStatus?.excludeFromActiveList;

  const missingRequiredFields = useMemo(() => {
    if (!isCompleting) return [];

    return fields
      .filter((field) => field.fieldRules?.required)
      // A field in a table is answered per row, so it is checked by the row check
      // below. Its plain form name is always empty, which would read as missing.
      .filter((field) => !tableFieldIds.has(field.id))
      .filter((field) =>
        isCustomFieldValueEmpty(serializeCustomFieldFormValue(field, readFormValue(field)))
      )
      .map((field) => field.fieldName);
  }, [isCompleting, fields, readFormValue, tableFieldIds]);

  // The same check for table rows, named so the person knows which row to fix.
  const missingRequiredRowFields = useMemo(() => {
    if (!isCompleting) return [];

    const missing = [];

    tableGroups.forEach((group) => {
      const requiredFields = group.fields.filter((field) => field.fieldRules?.required);
      if (requiredFields.length === 0) return;

      const rows = rowCounts[group.id] ?? 1;
      let filledRows = 0;

      for (let row = 1; row <= rows; row += 1) {
        if (isRowEmpty(group, row)) continue;
        filledRows += 1;

        requiredFields
          .filter((field) =>
            isCustomFieldValueEmpty(
              serializeCustomFieldFormValue(field, readRowValue(field, row))
            )
          )
          .forEach((field) => missing.push(`${group.name} — Row ${row}: ${field.fieldName}`));
      }

      // The API refuses to complete a table that has a required field but no rows.
      if (filledRows === 0) missing.push(`${group.name}: at least one row`);
    });

    return missing;
  }, [isCompleting, tableGroups, rowCounts, isRowEmpty, readRowValue]);

  const findings = useMemo(
    () => (hasRating ? calculateFindings(fields, readFormValue) : null),
    [hasRating, fields, readFormValue]
  );

  // Row answers score too, so they are counted and added to the totals above.
  const tableFindings = useMemo(() => {
    if (!hasRating) return null;

    const totals = { red: 0, amber: 0, green: 0 };

    tableGroups.forEach((group) => {
      const rows = rowCounts[group.id] ?? 1;

      for (let row = 1; row <= rows; row += 1) {
        const rowTotals = calculateFindings(group.fields, (field) => readRowValue(field, row));
        totals.red += rowTotals.red ?? 0;
        totals.amber += rowTotals.amber ?? 0;
        totals.green += rowTotals.green ?? 0;
      }
    });

    return totals;
  }, [hasRating, tableGroups, rowCounts, readRowValue]);

  const allFindings = useMemo(() => {
    if (!findings) return null;
    if (!tableFindings) return findings;

    return {
      ...findings,
      red: (findings.red ?? 0) + tableFindings.red,
      amber: (findings.amber ?? 0) + tableFindings.amber,
      green: (findings.green ?? 0) + tableFindings.green,
    };
  }, [findings, tableFindings]);

  // A completed document keeps the rows it was completed with, so rows can only be
  // added or removed while it is still in progress.
  const rowsLocked = !!existing?.isComplete;

  const addTableRow = (groupId) =>
    setRowCounts((previous) => ({ ...previous, [groupId]: (previous[groupId] ?? 1) + 1 }));

  // The rows below the removed one move up, so the numbering stays continuous.
  const removeTableRow = (group, removedRow) => {
    const rows = rowCounts[group.id] ?? 1;
    const values = {};

    for (let row = removedRow; row < rows; row += 1) {
      group.fields.forEach((field) => {
        values[rowFieldFormName(field.id, row)] = form.getFieldValue(
          rowFieldFormName(field.id, row + 1)
        );
      });
    }

    group.fields.forEach((field) => {
      values[rowFieldFormName(field.id, rows)] = undefined;
    });

    form.setFieldsValue(values);
    setRowCounts((previous) => ({ ...previous, [group.id]: Math.max(1, rows - 1) }));
  };

  const confirmRemoveTableRow = (group, row) =>
    Modal.confirm({
      title: `Remove row ${row}?`,
      content: 'Anything filled in on this row will be cleared.',
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: () => removeTableRow(group, row),
    });

  const missingEvidence = useMemo(() => {
    if (!hasRating || !isCompleting) return [];

    return fields
      .filter(fieldSupportsEvidence)
      .filter((field) => {
        const color = getAnswerColor(field, readFormValue(field));
        if (!isEvidenceRequired(color)) return false;

        return (
          (fieldCollectsComment(field) && !evidence[field.id]?.comment?.trim())
          || (fieldCollectsImage(field) && !hasPhoto(field.id))
        );
      })
      .map((field) => field.fieldName);
  }, [hasRating, isCompleting, fields, evidence, hasPhoto, readFormValue]);

  const missingRequiredPhotos = useMemo(() => {
    if (!hasRating || !isCompleting) return [];

    return fields
      .filter(fieldRequiresImage)
      .filter((field) => !hasPhoto(field.id))
      .map((field) => field.fieldName);
  }, [hasRating, isCompleting, fields, hasPhoto]);

  const missingRequiredComments = useMemo(() => {
    if (!hasRating || !isCompleting) return [];

    return fields
      .filter(fieldRequiresComment)
      .filter((field) => !evidence[field.id]?.comment?.trim())
      .map((field) => field.fieldName);
  }, [hasRating, isCompleting, fields, evidence]);

  const isBlocked =
    missingRequiredFields.length > 0 ||
    missingRequiredRowFields.length > 0 ||
    missingEvidence.length > 0 ||
    missingRequiredPhotos.length > 0 ||
    missingRequiredComments.length > 0;

  const needsCompany = !isEdit && !companyId;

  const buildFieldValues = useCallback(
    (values) =>
      fields.map((field) => {
        const serialized = serializeCustomFieldFormValue(field, values[fieldFormName(field.id)]);
        const held = evidence[field.id];

        if (!hasRating || !fieldCollectsComment(field) || !held?.comment?.trim()) {
          return { documentTemplateCustomFieldId: field.id, fieldValues: serialized };
        }

        return {
          documentTemplateCustomFieldId: field.id,
          fieldValues: { value: serialized, comment: held.comment.trim() },
        };
      }),
    [fields, evidence, hasRating]
  );

  // One entry per field per started row, numbered from 1. Rows nobody typed in
  // are skipped, and the numbering closes up behind them.
  const buildRowFieldValues = useCallback(
    (values) => {
      const entries = [];

      tableGroups.forEach((group) => {
        const rows = rowCounts[group.id] ?? 1;
        let rowIndex = 0;

        for (let row = 1; row <= rows; row += 1) {
          if (isRowEmpty(group, row)) continue;
          rowIndex += 1;

          group.fields.forEach((field) => {
            entries.push({
              documentTemplateCustomFieldId: field.id,
              rowIndex,
              fieldValues: serializeCustomFieldFormValue(
                field,
                values[rowFieldFormName(field.id, row)]
              ),
            });
          });
        }
      });

      return entries;
    },
    [tableGroups, rowCounts, isRowEmpty]
  );

  // What actually gets sent. A field inside a table has no plain answer — the API
  // rejects one without a row number — so those are left out here.
  const buildAllFieldValues = useCallback(
    (values) => [
      ...buildFieldValues(values).filter(
        (entry) => !tableFieldIds.has(entry.documentTemplateCustomFieldId)
      ),
      ...buildRowFieldValues(values),
    ],
    [buildFieldValues, buildRowFieldValues, tableFieldIds]
  );

  const stagedImages = useCallback(
    () =>
      Object.entries(evidence).flatMap(([fieldId, held]) =>
        (held.files ?? [])
          .filter((file) => file.originFileObj)
          .map((file) => ({
            uid: file.uid,
            fieldKey: fieldId,
            name: file.name,
            type: file.originFileObj.type,
            blob: file.originFileObj,
          }))
      ),
    [evidence]
  );

  const persistDraft = useCallback(
    async ({ formValues, ...overrides } = {}) => {
      // formValues lets the on-leave save pass a snapshot taken while the fields
      // were still mounted; otherwise read the live form.
      const values = formValues ?? form.getFieldsValue();
      const fieldValues = buildAllFieldValues(values);
      const images = stagedImages();

      // Nothing typed, said or attached is not a draft, it is an empty form.
      const isEmpty =
        images.length === 0 &&
        fieldValues.every(
          (entry) =>
            !entry.fieldValues?.comment &&
            isCustomFieldValueEmpty(entry.fieldValues?.value ?? entry.fieldValues)
        );

      if (isEmpty && !overrides.intent) return false;

      await putDraft({
        draftId: draftIdRef.current,
        syncState: syncStateRef.current,
        documentId: isEdit ? routeDocumentId : null,
        templateId: existing?.documentTemplateId ?? routeTemplateId,
        companyId,
        relationId: relationId ?? null,
        statusId: statusId ?? null,
        name: values.documentName ?? '',
        fieldValues,
        savedImages,
        form: { fields, statuses, typeName, hasRating, companies },
        router: {
        templateName, lockedCompanyId, relationId, returnTo,
        completedReturnTo, inProgressReturnTo,
      },
        ...overrides,
      });

      await syncDraftImages(draftIdRef.current, images);

      if (overrides.syncState) syncStateRef.current = overrides.syncState;
      return true;
    },
    [
      form, buildAllFieldValues, stagedImages, isEdit, routeDocumentId, routeTemplateId,
      existing, companyId, relationId, statusId, savedImages, fields, statuses,
      typeName, hasRating, companies, templateName, lockedCompanyId, returnTo,
      completedReturnTo, inProgressReturnTo,
    ]
  );

  // Keep a copy on the device as the user types, so closing the app, losing
  // signal or a browser tab being discarded does not cost them the form.
  useEffect(() => {
    if (loading || unavailableOffline || !seededRef.current) return;

    const handle = setTimeout(() => {
      if (finalizedRef.current) return;

      const unchanged =
        signatureOf(form.getFieldsValue(), evidence, statusId, companyId)
          + rowSignatureOf(form.getFieldsValue()) === baselineRef.current;

      if (unchanged) {
        if (persistedRef.current && !resumeDraftId && syncStateRef.current === SYNC_LOCAL) {
          persistedRef.current = false;
          deleteDraft(draftIdRef.current).catch((error) =>
            console.error('Failed to drop the unchanged draft', error)
          );
        }
        return;
      }

      persistDraft()
        .then((saved) => {
          if (saved) persistedRef.current = true;
        })
        .catch((error) => {
          console.error('Failed to save this document on the device', error);
          message.error('Could not save on this device. Storage may be full.');
        });
    }, 1200);

    return () => clearTimeout(handle);
  }, [
    loading, unavailableOffline, watchedValues, evidence, persistDraft, signatureOf,
    rowSignatureOf, form, statusId, companyId, resumeDraftId,
  ]);

  // The save above is debounced and its timer is cancelled when the screen is
  // left, so anything typed in the last second would be lost on the way out.
  // This keeps the newest version in a ref and writes it once more on leaving.
  const saveOnLeaveRef = useRef(null);

  // Read the form now, while the fields are still mounted. On the back button this
  // save runs during unmount, after the inputs (vehicle, badges, …) have torn down,
  // so a fresh getFieldsValue() there would miss them and overwrite the draft with
  // blanks. Capturing at render time keeps the on-leave save complete.
  const leaveValues = form.getFieldsValue();

  saveOnLeaveRef.current = () => {
    if (loading || unavailableOffline || !seededRef.current || finalizedRef.current) return;

    const unchanged =
      signatureOf(leaveValues, evidence, statusId, companyId)
        + rowSignatureOf(leaveValues) === baselineRef.current;

    if (unchanged) return;

    persistDraft({ formValues: leaveValues }).catch((error) =>
      console.error('Failed to save this document on the way out', error)
    );
  };

  useEffect(() => {
    const saveNow = () => saveOnLeaveRef.current?.();

    // Leaving the page, and the app being hidden or closed on a phone.
    window.addEventListener('pagehide', saveNow);

    return () => {
      window.removeEventListener('pagehide', saveNow);
      saveNow();
    };
  }, []);

  const destinationFor = useCallback(
    (statusToSave) => {
      const finished = statuses.find((status) => status.id === statusToSave)?.excludeFromActiveList;
      const moved = finished ? completedReturnTo : inProgressReturnTo;
      return moved || returnTo || '/documents/in-progress';
    },
    [statuses, completedReturnTo, inProgressReturnTo, returnTo]
  );

  const queueForUpload = useCallback(
    async (statusToSave, note) => {
      await persistDraft({
        statusId: statusToSave,
        intent: statusToSave === inspectionStatuses.complete?.id ? 'complete' : 'draft',
        syncState: SYNC_PENDING,
      });

      finalizedRef.current = true;
      persistedRef.current = true;
      await enqueueDraft(draftIdRef.current);
      message.success(note);

      navigate(inProgressReturnTo || returnTo || '/documents/in-progress', { replace: true });
    },
    [persistDraft, inspectionStatuses, navigate, inProgressReturnTo, returnTo]
  );

  const handleRemoveSavedImage = async (imageId) => {
    if (navigator.onLine === false) {
      message.info('Removing a saved photo needs a connection.');
      return;
    }

    try {
      await documentService.deleteDocumentImage(routeDocumentId, imageId);
      setSavedImages((previous) => previous.filter((image) => image.imageId !== imageId));
      message.success('Photo removed');
    } catch (error) {
      console.error('Failed to remove photo', error);
      message.error('Failed to remove photo');
    }
  };

  const handleSave = async (statusToSave = statusId ?? null) => {
    // A draft is deliberately exempt from the completion blockers -- it is saved
    // precisely because it is not finished yet.
    const completing = !isInspection || statusToSave === inspectionStatuses.complete?.id;
    if ((completing && isBlocked) || needsCompany || saving) return;

    if (isCompressing) {
      message.info('Still compressing photos, one moment.');
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSaving(true);

    try {
      if (navigator.onLine === false) {
        await queueForUpload(statusToSave, 'Saved on this device. It will upload when you have signal.');
        return;
      }

      const fieldValues = buildAllFieldValues(values);

      // Photos ride inside the save body; there is no separate upload call. The
      // location + timestamp are already burned on at capture time (FieldEvidence).
      const images = await toBase64Payload(
        Object.fromEntries(
          Object.entries(evidence)
            .filter(([, held]) => held.files.length > 0)
            .map(([fieldId, held]) => [fieldId, held.files])
        )
      );

      if (isEdit) {
        await documentService.updateDocument(routeDocumentId, {
          name: values.documentName,
          documentTemplateStatusId: statusToSave,
          fieldValues,
          images,
        });
      } else {
        await documentService.createDocument({
          clientRequestId: draftIdRef.current,
          documentTemplateId: routeTemplateId,
          name: values.documentName,
          companyId,
          relationId,
          documentTemplateStatusId: statusToSave,
          fieldValues,
          images,
        });
      }

      finalizedRef.current = true;
      await deleteDraft(draftIdRef.current);

      message.success('Document saved');
      navigate(destinationFor(statusToSave), { replace: true });
    } catch (error) {
      console.error('Failed to save document', error);

      if (isRetryableError(error)) {
        await queueForUpload(statusToSave, 'Saved on this device. It will upload when you have signal.');
        return;
      }

      message.error('Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleViewPdf = async () => {
    // A Word-template document is filled and converted on request, which takes a
    // few seconds, so say something while it happens.
    const loading = existing?.usesWordTemplate ? message.loading('Preparing the PDF...', 0) : null;

    try {
      const url = existing?.usesWordTemplate
        ? await documentService.getDocumentWordPdf(routeDocumentId)
        : await documentService.getDocumentPdf(routeDocumentId);

      if (!url) {
        message.info('No PDF has been generated for this document yet.');
        return;
      }
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      console.error('Failed to open PDF', error);
      message.error('Failed to open PDF');
    } finally {
      loading?.();
    }
  };

  if (loading) {
    return (
      <Card className="document-fill__card">
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  if (unavailableOffline) {
    return (
      <Card className="document-fill__card">
        <Result
          status="warning"
          title="Not available offline"
          subTitle="This document has not been opened on this device, so there is no copy to work from. Try again when you have a connection."
          extra={
            <Button type="primary" onClick={() => navigate(returnTo ?? '/documents/in-progress')}>
              Back to documents
            </Button>
          }
        />
      </Card>
    );
  }

  const renderField = (field) => {
    const color = hasRating ? getAnswerColor(field, readFormValue(field)) : undefined;
    const showEvidence = hasRating && fieldSupportsEvidence(field);

    return (
      <div className="document-fill__field" key={field.id}>
        <Form.Item
          name={fieldFormName(field.id)}
          valuePropName={getCustomFieldValuePropName(field)}
          label={
            <span className="document-fill__label">
              <span className="document-fill__label-text">
                {field.fieldName}
                {field.fieldRules?.required && <span className="document-fill__required"> *</span>}
              </span>
              <QuestionStatusBadge color={color} />
            </span>
          }
          extra={field.fieldHint || undefined}
        >
          {renderCustomFieldInput(field, { companyId, hasRating })}
        </Form.Item>

        {showEvidence && (
          <FieldEvidence
            savedImages={savedImagesByField[field.id] ?? []}
            evidence={readEvidence(field.id)}
            evidenceRequired={isEvidenceRequired(color)}
            photoRequired={fieldRequiresImage(field)}
            commentRequired={fieldRequiresComment(field)}
            showImages={fieldCollectsImage(field)}
            showComment={fieldCollectsComment(field)}
            onChange={(update) =>
              setEvidence((previous) => ({
                ...previous,
                [field.id]: update(previous[field.id] ?? EMPTY_EVIDENCE),
              }))
            }
            onRemoveSaved={isEdit ? handleRemoveSavedImage : undefined}
          />
        )}
      </div>
    );
  };

  // A field inside a table row. Same input as anywhere else, but under its row's
  // form name, and with no photo or comment block — tables do not collect those.
  const renderRowField = (field, row) => (
    <Form.Item
      key={`${field.id}-${row}`}
      name={rowFieldFormName(field.id, row)}
      valuePropName={getCustomFieldValuePropName(field)}
      label={
        <span className="document-fill__label-text">
          {field.fieldName}
          {field.fieldRules?.required && <span className="document-fill__required"> *</span>}
        </span>
      }
      extra={field.fieldHint || undefined}
    >
      {renderCustomFieldInput(field, { companyId, hasRating })}
    </Form.Item>
  );

  // One card per captured row, numbered from 1.
  const renderTableRows = (group) => {
    const rows = rowCounts[group.id] ?? 1;

    return Array.from({ length: rows }, (_, index) => index + 1).map((row) => (
      <Card
        key={row}
        size="small"
        className="document-fill__row-card"
        title={`Row ${row}`}
        extra={
          rows > 1 && !rowsLocked ? (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              aria-label={`Remove row ${row}`}
              onClick={() => confirmRemoveTableRow(group, row)}
            />
          ) : undefined
        }
      >
        {group.fields.map((field) => renderRowField(field, row))}
      </Card>
    ));
  };

  return (
    <div className="document-fill">
      <Card className="document-fill__card">
        <Form form={form} layout="vertical">
          <Form.Item
            name="documentName"
            label="Document name"
            rules={[{ required: true, message: 'Give this document a name' }]}
          >
            <Input placeholder="Document name" />
          </Form.Item>

          {isEdit || lockedCompanyId ? (
            <Form.Item label="Company">
              <Input value={lockedCompanyName} disabled />
            </Form.Item>
          ) : (
            <Form.Item
              label="Company"
              required
              validateStatus={needsCompany ? 'error' : undefined}
              help={needsCompany ? 'Choose the company this document is for' : undefined}
            >
              <Select
                showSearch
                optionFilterProp="label"
                value={companyId}
                placeholder="Select a company"
                options={companies.map((company) => ({
                  value: company.id,
                  label: company.name,
                }))}
                onChange={setCompanyId}
              />
            </Form.Item>
          )}

          {!isInspection && (
            <Form.Item label="Status">
              <Select
                value={statusId}
                onChange={setStatusId}
                options={[...statuses]
                  .sort((a, b) => (a.statusOrder ?? 0) - (b.statusOrder ?? 0))
                  .map((status) => ({
                    value: status.id,
                    label: status.excludeFromActiveList
                      ? `${status.statusName} (completes)`
                      : status.statusName,
                  }))}
              />
            </Form.Item>
          )}

          {hasRating && allFindings && (
            <div className="document-fill__findings">
              <Text type="secondary">Findings</Text>
              <FindingsStrip counts={allFindings} />
            </div>
          )}

          {fieldGroups.map((group) => (
            <Card
              key={group.id}
              size="small"
              className="document-fill__group"
              title={
                <Space direction="vertical" size={0}>
                  <Title level={5} style={{ margin: 0 }}>
                    {group.name}
                  </Title>
                  {group.hint && <Text type="secondary">{group.hint}</Text>}
                </Space>
              }
            >
              {group.isRepeating
                ? renderTableRows(group)
                : group.fields.map((field, index) => (
                    <React.Fragment key={field.id}>
                      {renderField(field)}
                      {index < group.fields.length - 1 && (
                        <Divider style={{ margin: '40px 0', borderColor: '#d9d9d9' }} />
                      )}
                    </React.Fragment>
                  ))}

              {/* Under the rows, not above them: a new row lands right where the
                  button is, so there is nothing to scroll back to. */}
              {group.isRepeating && !rowsLocked && (
                <Button
                  block
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => addTableRow(group.id)}
                >
                  Add row
                </Button>
              )}
            </Card>
          ))}

          {isCompleting && isBlocked && (
            <Alert
              type="error"
              className="document-fill__blockers"
              message={
                <div>
                  <Title level={5} style={{ margin: 0 }}>
                    Before this document can be completed
                  </Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    This status marks the document complete, so these must be resolved first.
                  </Text>

                  {missingRequiredFields.length > 0 && (
                    <>
                      <Text strong className="document-fill__blocker-heading">
                        Required fields
                      </Text>
                      <ul className="document-fill__blocker-list">
                        {missingRequiredFields.map((name) => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {missingRequiredRowFields.length > 0 && (
                    <>
                      <Text strong className="document-fill__blocker-heading">
                        Required fields in tables
                      </Text>
                      <ul className="document-fill__blocker-list">
                        {missingRequiredRowFields.map((name) => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {missingEvidence.length > 0 && (
                    <>
                      <Text strong className="document-fill__blocker-heading">
                        Red or amber answers needing a comment and photo
                      </Text>
                      <ul className="document-fill__blocker-list">
                        {missingEvidence.map((name) => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {missingRequiredPhotos.length > 0 && (
                    <>
                      <Text strong className="document-fill__blocker-heading">
                        Answers needing a photo
                      </Text>
                      <ul className="document-fill__blocker-list">
                        {missingRequiredPhotos.map((name) => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              }
            />
          )}
        </Form>
      </Card>

      <div className="document-fill__actions">
        {isCompressing && (
          <Text type="secondary" className="document-fill__status-note">
            Compressing photos...
          </Text>
        )}
        {existing?.isComplete && (
          <Button block onClick={handleViewPdf} disabled={saving}>
            View PDF
          </Button>
        )}
        {isInspection ? (
          !existing?.isComplete && (
          <>
            <Button
              block
              loading={saving}
              disabled={needsCompany || isCompressing || !inspectionStatuses.draft}
              onClick={() => handleSave(inspectionStatuses.draft?.id ?? null)}
            >
              Save Draft
            </Button>
            <Button
              type="primary"
              block
              loading={saving}
              disabled={isBlocked || needsCompany || isCompressing || !inspectionStatuses.complete}
              onClick={() =>
                Modal.confirm({
                  title: 'Complete this inspection?',
                  content: 'Once completed it can no longer be edited.',
                  icon: null,
                  okText: 'Complete',
                  cancelText: 'Cancel',
                  okButtonProps: {
                    style: {
                      background: 'var(--primary-color)',
                      borderColor: 'var(--primary-color)',
                    },
                  },
                  onOk: () => handleSave(inspectionStatuses.complete?.id ?? null),
                })
              }
            >
              Complete
            </Button>
            {(!inspectionStatuses.draft || !inspectionStatuses.complete) && (
              <Text type="danger" className="document-fill__status-note">
                This template is missing its{' '}
                {!inspectionStatuses.draft ? 'in-progress' : 'completing'} status, so it cannot be
                saved that way.
              </Text>
            )}
          </>
          )
        ) : (
          <Button
            type="primary"
            block
            loading={saving}
            disabled={isBlocked || needsCompany || isCompressing}
            onClick={() => handleSave()}
          >
            {isCompleting ? 'Save and complete' : 'Save in progress'}
          </Button>
        )}
        {isEdit && existing?.currentStatusName && (
          <div className="document-fill__status-tag">
            <Tag color={existing.isComplete ? 'green' : 'orange'}>{existing.currentStatusName}</Tag>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentFill;
